import { useCallback, useEffect, useState } from 'react';
import ReactSelect from 'react-select';
import type { StylesConfig } from 'react-select';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { DefaultSelectStyle } from 'components/Shared/Forms/CompactSelect/styles';
import type {
  YardEligibleFlowDto,
  YardJourneyDto,
  YardJourneyEnvelopeDto,
  YardJourneyFieldAnswerInput,
  YardJourneyFieldDto,
  YardJourneyStepDto,
} from 'contracts/YardJourneyExecution';
import type { YardGateEntry } from '../../adapter';
import * as S from '../../styles';

export const YARD_GATE_START_JOURNEY_NO_FLOW_ERROR =
  'Selecione o fluxo da jornada.';
export const YARD_GATE_START_JOURNEY_NOT_PORTARIA_ERROR =
  'A etapa atual do fluxo não é a portaria.';
export const YARD_GATE_START_JOURNEY_COORDINATES_ERROR =
  'Desmarque "Exige coordenadas" na parametrização da etapa.';
export const YARD_GATE_START_JOURNEY_DONE_ERROR =
  'A jornada desta ordem já foi concluída.';
export const YARD_GATE_START_JOURNEY_LOAD_ERROR =
  'Não foi possível carregar a jornada da ordem.';
export const YARD_GATE_START_JOURNEY_REQUIRED_ERROR =
  'Responda todos os campos obrigatórios.';

type FieldValue = string | number | boolean | number[] | undefined;

interface SelectOption {
  value: number;
  label: string;
}

const selectStyle: StylesConfig<SelectOption, false> = {
  ...(DefaultSelectStyle as StylesConfig<SelectOption, false>),
  menuPortal: (base) => ({ ...base, zIndex: 9999, pointerEvents: 'auto' }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

export const isPortariaStep = (step: YardJourneyStepDto): boolean =>
  step.type === 'PORTARIA';

export const journeyHasPortariaStep = (journey: YardJourneyDto): boolean =>
  journey.steps.some((step) => step.type === 'PORTARIA');

export const currentJourneyStep = (
  journey: YardJourneyDto
): YardJourneyStepDto | null =>
  journey.steps.find((step) => step.event.status !== 'COMPLETED') ?? null;

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const hasValue = (field: YardJourneyFieldDto, value: FieldValue): boolean => {
  switch (field.type) {
    case 'SELECT':
      return toNumber(value) !== null;
    case 'CHECKBOX_GROUP':
      return Array.isArray(value) && value.length > 0;
    case 'CHECKBOX':
      return typeof value === 'boolean';
    default:
      return value !== undefined && value !== null && value !== '';
  }
};

const buildAnswers = (
  fields: YardJourneyFieldDto[],
  values: Record<number, FieldValue>
): YardJourneyFieldAnswerInput[] =>
  fields.flatMap((field): YardJourneyFieldAnswerInput[] => {
    const value = values[field.id];
    if (!hasValue(field, value)) return [];
    if (field.type === 'SELECT') {
      return [{ fieldId: field.id, optionIds: [toNumber(value) as number] }];
    }
    if (field.type === 'CHECKBOX_GROUP') {
      return [{ fieldId: field.id, optionIds: value as number[] }];
    }
    return [{ fieldId: field.id, value: value as string | number | boolean }];
  });

const inputTypeFor = (type: YardJourneyFieldDto['type']): string => {
  if (type === 'DATE') return 'date';
  if (type === 'TIME') return 'time';
  if (type === 'DATETIME') return 'datetime-local';
  return 'text';
};

interface StartJourneyModalProps {
  entry: YardGateEntry;
  isBusy: boolean;
  onClose: () => void;
  getJourney: (orderId: number) => Promise<YardJourneyEnvelopeDto>;
  confirmJourney: (
    orderId: number,
    yardFlowId: number
  ) => Promise<YardJourneyDto>;
  onSubmit: (
    entry: YardGateEntry,
    params: {
      currentStepId: number;
      fieldAnswers: YardJourneyFieldAnswerInput[];
    }
  ) => Promise<boolean>;
  onFlowConfirmed: (entry: YardGateEntry) => Promise<void> | void;
}

export const StartJourneyModal = ({
  entry,
  isBusy,
  onClose,
  getJourney,
  confirmJourney,
  onSubmit,
  onFlowConfirmed,
}: StartJourneyModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [journey, setJourney] = useState<YardJourneyDto | null>(null);
  const [currentStep, setCurrentStep] = useState<YardJourneyStepDto | null>(
    null
  );
  const [eligibleFlows, setEligibleFlows] = useState<YardEligibleFlowDto[]>([]);
  const [selectedFlowId, setSelectedFlowId] = useState<number | null>(null);
  const [values, setValues] = useState<Record<number, FieldValue>>({});

  const isDisabled = isBusy || isSubmitting;
  const isDismissalBlocked = isLoading || isDisabled;

  const applyJourney = useCallback((loaded: YardJourneyDto) => {
    const step = currentJourneyStep(loaded);
    if (!step) {
      setCurrentStep(null);
      setError(YARD_GATE_START_JOURNEY_DONE_ERROR);
      return;
    }
    if (!isPortariaStep(step)) {
      setCurrentStep(null);
      setError(YARD_GATE_START_JOURNEY_NOT_PORTARIA_ERROR);
      return;
    }
    setJourney(loaded);
    setCurrentStep(step);
    setValues({});
    setError('');
  }, []);

  useEffect(() => {
    let isActive = true;

    const load = async () => {
      if (entry.orderId == null) {
        setError(YARD_GATE_START_JOURNEY_LOAD_ERROR);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const envelope = await getJourney(entry.orderId);
        if (!isActive) return;
        setEligibleFlows(envelope.eligibleFlows);
        setSelectedFlowId(
          envelope.suggestedFlowId ?? envelope.eligibleFlows[0]?.id ?? null
        );
        if (envelope.journey) applyJourney(envelope.journey);
      } catch {
        if (isActive) setError(YARD_GATE_START_JOURNEY_LOAD_ERROR);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    void load();
    return () => {
      isActive = false;
    };
  }, [entry.orderId, getJourney, applyJourney]);

  const continueJourney = async () => {
    if (isDisabled) return;
    if (entry.orderId == null || selectedFlowId == null) {
      setError(YARD_GATE_START_JOURNEY_NO_FLOW_ERROR);
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      const loaded = await confirmJourney(entry.orderId, selectedFlowId);
      if (!journeyHasPortariaStep(loaded)) {
        await onFlowConfirmed(entry);
        onClose();
        return;
      }
      applyJourney(loaded);
    } catch {
      setError(YARD_GATE_START_JOURNEY_LOAD_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fields = currentStep?.fields ?? [];
  const hasAllRequiredAnswers = fields
    .filter((field) => field.required)
    .every((field) => hasValue(field, values[field.id]));

  const save = async () => {
    if (isDisabled || entry.orderId == null || !currentStep) return;
    if (currentStep.hasCoordinates) {
      setError(YARD_GATE_START_JOURNEY_COORDINATES_ERROR);
      return;
    }
    if (!hasAllRequiredAnswers) {
      setError(YARD_GATE_START_JOURNEY_REQUIRED_ERROR);
      return;
    }

    setIsSubmitting(true);
    setError('');
    const saved = await onSubmit(entry, {
      currentStepId: currentStep.id,
      fieldAnswers: buildAnswers(fields, values),
    });
    setIsSubmitting(false);
    if (saved) onClose();
  };

  const flowOptions: SelectOption[] = eligibleFlows.map((flow) => ({
    value: flow.id,
    label: flow.name,
  }));

  const renderField = (field: YardJourneyFieldDto) => {
    const inputId = `start-journey-${entry.id}-${field.id}`;
    const label = field.required ? `${field.label} *` : field.label;
    const value = values[field.id];
    const setFieldValue = (next: FieldValue) =>
      setValues((current) => ({ ...current, [field.id]: next }));

    if (field.type === 'SELECT') {
      const options: SelectOption[] = field.options.map((option) => ({
        value: Number(option.id),
        label: option.label,
      }));
      return (
        <S.Field key={field.id}>
          <S.Label htmlFor={inputId}>{label}</S.Label>
          <ReactSelect<SelectOption, false>
            inputId={inputId}
            aria-label={field.label}
            options={options}
            value={
              options.find((option) => option.value === toNumber(value)) ?? null
            }
            isDisabled={isDisabled}
            isClearable={!field.required}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            styles={selectStyle}
            placeholder={field.placeholder ?? 'Selecione'}
            onChange={(option) =>
              setFieldValue(option ? option.value : undefined)
            }
          />
        </S.Field>
      );
    }

    if (field.type === 'CHECKBOX_GROUP') {
      const selected = Array.isArray(value) ? value : [];
      return (
        <S.Field key={field.id}>
          <S.Label as="span">{label}</S.Label>
          <div
            role="group"
            aria-label={field.label}
            className="flex flex-col gap-1"
          >
            {field.options.map((option) => (
              <label
                key={option.id}
                className="inline-flex items-center gap-2 text-sm text-slate-700"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option.id)}
                  disabled={isDisabled}
                  onChange={() =>
                    setFieldValue(
                      selected.includes(option.id)
                        ? selected.filter((id) => id !== option.id)
                        : [...selected, option.id]
                    )
                  }
                />
                {option.label}
              </label>
            ))}
          </div>
        </S.Field>
      );
    }

    if (field.type === 'CHECKBOX') {
      return (
        <S.Field key={field.id}>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              aria-label={field.label}
              checked={value === true}
              disabled={isDisabled}
              onChange={(event) => setFieldValue(event.target.checked)}
            />
            {label}
          </label>
        </S.Field>
      );
    }

    if (field.type === 'TEXTAREA') {
      return (
        <S.Field key={field.id}>
          <S.Label htmlFor={inputId}>{label}</S.Label>
          <S.Textarea
            id={inputId}
            aria-label={field.label}
            value={typeof value === 'string' ? value : ''}
            placeholder={field.placeholder ?? undefined}
            maxLength={
              typeof field.settings?.maxLength === 'number'
                ? field.settings.maxLength
                : undefined
            }
            disabled={isDisabled}
            onChange={(event) => setFieldValue(event.target.value)}
          />
        </S.Field>
      );
    }

    if (field.type === 'NUMBER') {
      return (
        <S.Field key={field.id}>
          <S.Label htmlFor={inputId}>{label}</S.Label>
          <S.Input
            id={inputId}
            type="number"
            aria-label={field.label}
            value={typeof value === 'number' ? value : ''}
            min={
              typeof field.settings?.min === 'number'
                ? field.settings.min
                : undefined
            }
            max={
              typeof field.settings?.max === 'number'
                ? field.settings.max
                : undefined
            }
            step={
              typeof field.settings?.step === 'number'
                ? field.settings.step
                : undefined
            }
            disabled={isDisabled}
            onChange={(event) =>
              setFieldValue(
                event.target.value === ''
                  ? undefined
                  : Number(event.target.value)
              )
            }
          />
        </S.Field>
      );
    }

    return (
      <S.Field key={field.id}>
        <S.Label htmlFor={inputId}>{label}</S.Label>
        <S.Input
          id={inputId}
          type={inputTypeFor(field.type)}
          aria-label={field.label}
          value={typeof value === 'string' ? value : ''}
          placeholder={field.placeholder ?? undefined}
          disabled={isDisabled}
          onChange={(event) => setFieldValue(event.target.value)}
        />
      </S.Field>
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isDismissalBlocked) onClose();
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        className="bg-white sm:max-w-[520px]"
        onEscapeKeyDown={(event) => {
          if (isDismissalBlocked) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (isDismissalBlocked) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Iniciar jornada</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {isLoading ? (
            <S.Field>Carregando jornada...</S.Field>
          ) : (
            <>
              {journey == null ? (
                <S.Field>
                  <S.Label htmlFor={`start-journey-flow-${entry.id}`}>
                    Fluxo
                  </S.Label>
                  <ReactSelect<SelectOption, false>
                    inputId={`start-journey-flow-${entry.id}`}
                    aria-label="Fluxo"
                    options={flowOptions}
                    value={
                      flowOptions.find(
                        (option) => option.value === selectedFlowId
                      ) ?? null
                    }
                    isDisabled={isDisabled || flowOptions.length === 0}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    styles={selectStyle}
                    placeholder="Selecione"
                    onChange={(option) =>
                      setSelectedFlowId(option ? option.value : null)
                    }
                  />
                </S.Field>
              ) : null}

              <div className="max-h-[55vh] space-y-4 overflow-y-auto">
                {fields.map(renderField)}
              </div>

              {currentStep?.hasCoordinates ? (
                <S.FieldError>
                  {YARD_GATE_START_JOURNEY_COORDINATES_ERROR}
                </S.FieldError>
              ) : null}
              {error ? <S.FieldError>{error}</S.FieldError> : null}

              <DialogFooter>
                <S.Button
                  mood="light"
                  type="button"
                  onClick={onClose}
                  disabled={isDisabled}
                >
                  Cancelar
                </S.Button>
                {journey == null ? (
                  <S.Button
                    type="button"
                    onClick={continueJourney}
                    disabled={isDisabled || selectedFlowId == null}
                  >
                    Continuar
                  </S.Button>
                ) : (
                  <S.Button
                    type="button"
                    onClick={save}
                    disabled={isDisabled || !hasAllRequiredAnswers}
                  >
                    Iniciar jornada
                  </S.Button>
                )}
              </DialogFooter>
            </>
          )}
          {!isLoading && journey == null && flowOptions.length === 0 ? (
            <S.FieldError>{YARD_GATE_START_JOURNEY_NO_FLOW_ERROR}</S.FieldError>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};
